import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  readNotificationPolicy,
  setNotificationPolicy,
} from '@/lib/api/generated/api';
import type {
  EscalationDelay,
  NotificationPolicy,
} from '@/lib/api/generated/model';
import { expectStatus } from '@/lib/api/client';

import { organizationKeys } from '../queryKeys';

/**
 * The organization's escalation delay and every change to it (service spec
 * 123). Org admins only; the service refuses anyone else.
 */
export function useNotificationPolicy() {
  return useQuery<NotificationPolicy>({
    queryKey: organizationKeys.notificationPolicy(),
    queryFn: async ({ signal }) => {
      const response = await readNotificationPolicy({ signal });
      return expectStatus(
        response,
        200,
        'Could not read the notification policy.',
      );
    },
    refetchOnWindowFocus: false,
  });
}

/** Change the delay. The answer carries the new history too, so it replaces the cache. */
export function useSetEscalationDelay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (delay: EscalationDelay) => {
      const response = await setNotificationPolicy({ escalation_delay: delay });
      return expectStatus(
        response,
        200,
        'The escalation setting could not be changed.',
      );
    },
    onSuccess: (policy) => {
      queryClient.setQueryData(organizationKeys.notificationPolicy(), policy);
    },
    onError: () => {
      toast.error('The escalation setting could not be changed.');
    },
  });
}
